import { useState, useEffect } from 'react';
import classNames from 'classnames';

import { useMainMachine } from '../../../utils/useMainMachine';
import { Select } from '../basic-ui/Select';
import { ACTIONS } from '../../../state/actions';
import { MODAL_ACTIONS } from '../../../modal/modalMachine';
import { ReferrerSegmentModal } from '../modals/ReferrerSegment';
import { URLParamsSegmentModal } from '../modals/URLParamsSegment';
import { UTMParamsSegmentModal } from '../modals/UTMParamsSegment';
import { GENERATING, SEGMENTS } from '../../constants';
import { generateSegment, parseSegment, getSegmentAsText } from '../../../utils/segments';

import EditIcon from '@crazyegginc/hatch/dist/images/icon-pencil-filled.svg?react';

const options = [
  { value: SEGMENTS.EVERYONE, label: 'Everyone', segmentFn: () => null },
  {
    value: SEGMENTS.NEW_VISITORS,
    label: 'New visitors',
    segmentFn: () => generateSegment([{ type: SEGMENTS.NEW_VISITORS }]),
  },
  {
    value: SEGMENTS.RETURNING_VISITORS,
    label: 'Returning visitors',
    segmentFn: () => generateSegment([{ type: SEGMENTS.RETURNING_VISITORS }]),
  },
  {
    value: SEGMENTS.DIRECT_VISITORS,
    label: 'Direct visitors',
    segmentFn: () => generateSegment([{ type: SEGMENTS.DIRECT_VISITORS }]),
  },
  {
    value: SEGMENTS.AD_TRAFFIC,
    label: 'Ad traffic',
    Modal: UTMParamsSegmentModal,
    segmentFn: (values) => generateSegment(values.map((p) => ({ type: SEGMENTS.AD_TRAFFIC, values: p }))),
  },
  {
    value: SEGMENTS.REFERRER,
    label: 'Referrer',
    Modal: ReferrerSegmentModal,
    segmentFn: (values) => generateSegment([{ type: SEGMENTS.REFERRER, values }]),
  },
  {
    value: SEGMENTS.URL_PARAMETER,
    label: 'URL parameter',
    Modal: URLParamsSegmentModal,
    segmentFn: (values) => generateSegment(values.map((p) => ({ type: SEGMENTS.URL_PARAMETER, values: p }))),
  },
];

export function SegmentSelector() {
  const { useSelector, machine } = useMainMachine();
  const savedSegment = useSelector(({ context }) => context.segment);
  const [value, setValue] = useState(parseSegment(savedSegment).type);

  const activePageId = useSelector(({ context }) => context.activePageId);
  const isPublished = useSelector(
    ({ context }) =>
      context.editor.pages.find((p) => p.id === activePageId)?.versions.find((v) => v.id === context.activeVersionId)
        ?.published_at != null,
  );
  const hasPermissions = useSelector(({ context }) => context.userId && context.hasPublishPermissions);
  const generating = useSelector((state) => state.hasTag(GENERATING));

  const canEdit = !isPublished || hasPermissions;

  useEffect(() => {
    setValue(parseSegment(savedSegment).type);
  }, [savedSegment]);

  function revertValue() {
    setValue(parseSegment(savedSegment).type);
  }

  function setSegment(segment) {
    const { name, details } = getSegmentAsText(segment);
    machine.send({
      type: ACTIONS.SET_SEGMENT,
      segment,
      segmentType: parseSegment(segment).type,
      description: `${name}\n${details}`,
    });
  }

  function closeModal() {
    machine.send({
      type: MODAL_ACTIONS.CLOSE,
    });
  }

  function handleSegmentChange(newValue) {
    if (generating) return;
    if (!canEdit) {
      machine.send({
        type: ACTIONS.SHOW_AUTH_MODAL,
      });
      return;
    }
    const { Modal, segmentFn } = options.find((o) => o.value === newValue);

    if (newValue === value) {
      if (Modal) {
        editSegment();
      }
      return;
    }

    setValue(newValue);

    if (Modal) {
      machine.send({
        type: MODAL_ACTIONS.SHOW,
        component: (
          <Modal
            onCancel={revertValue}
            onSubmit={(values) => {
              closeModal();
              setSegment(segmentFn(values));
            }}
          />
        ),
      });
    } else {
      setSegment(segmentFn());
    }
  }

  const { Modal, segmentFn } = options.find((o) => o.value === value);

  function editSegment() {
    if (generating) return;
    if (!canEdit) {
      machine.send({
        type: ACTIONS.SHOW_AUTH_MODAL,
      });
      return;
    }

    const { values } = parseSegment(savedSegment);

    machine.send({
      type: MODAL_ACTIONS.SHOW,
      component: (
        <Modal
          values={values}
          onCancel={revertValue}
          onSubmit={(newValues) => {
            closeModal();
            setSegment(segmentFn(newValues));
          }}
        />
      ),
    });
  }

  return (
    <div className="w-[173px] shrink-0 relative">
      <Select
        aria-label="segment selector"
        value={value}
        options={options}
        onChange={handleSegmentChange}
        disabled={generating}
      />
      {Modal ? (
        <button
          type="button"
          className={classNames(
            'text-gray-200 hover:text-white p-1',
            'items-center justify-center shrink-0',
            'absolute right-7 top-1.25 duration-75 hover:transition-all',
          )}
          onClick={editSegment}
          disabled={generating}
        >
          <EditIcon className="h-3 w-3 fill-current" aria-label="edit targeting" />
        </button>
      ) : null}
    </div>
  );
}
